import {
    IFormMetadata,
    RepositoryDataCollection,
    IMetadataAction,
    IViewMetadata,
    SaveHandlingType,
    RecalculateProperty,
    HeaderBadge,
} from "@tylertech/tyler-api-types";
import { ArrayHelpers } from "./ArrayHelpers";
import { IMenuOption } from "@tylertech/forge";
import { GuidHelper } from "@tylertech/shared-module";
import { CheckpointAction } from "../interfaces";

/**
 * Manages an ordered collection of `IFormMetadata`s with their corresponding `model`s and `RepositoryDataCollection`s.
 */
export class MetadataFormStack<TModel, TCollection extends RepositoryDataCollection> implements IFormMetadata
{
    private readonly _initialState: FormState<TModel, TCollection>;

    private readonly stack = new Stack<FormState<TModel, TCollection>>();

    constructor(initialForm: Readonly<IFormMetadata>, model: Readonly<TModel>, dataController: Readonly<TCollection>)
    {
        this._initialState = new FormState(initialForm, model, false, false, dataController);
        this.stack.push(this.initialState);
    }

    /** Returns a reference to the initial `FormState` this `FormStack` was created with. */
    get initialState(): FormState<TModel, TCollection>
    {
        return this._initialState;
    }

    get length(): number
    {
        return this.stack.length;
    }

    get previousState(): Readonly<FormState<TModel, TCollection>>
    {
        return this.stack.previous();
    }

    get current(): FormState<TModel, TCollection>
    {
        return this.stack.head();
    }

    /** Returns the `IFormMetadata` for the state at the head of the stack. */
    get form(): IFormMetadata
    {
        return this.current?.formMetadata;
    }

    /** Returns the `TModel` for the state at the head of the stack. */
    get model(): TModel
    {
        return this.current.model;
    }

    /** Returns the `TCollection` for the state at the head of the stack. */
    get dataCollection(): TCollection
    {
        return this.current.datacontroller;
    }

    /* Delegates to the head IFormMetadata's property. */ get Actions(): IMetadataAction[]
    {
        return this.form.Actions;
    }

    /* Delegates to the head IFormMetadata's property. */ get BoundType(): string
    {
        return this.form.BoundType;
    }

    /* Delegates to the head IFormMetadata's property. */ get ConstantFilter(): string
    {
        return this.form.ConstantFilter;
    }

    /* Delegates to the head IFormMetadata's property. */ get HasPermission(): boolean
    {
        return this.form.HasPermission;
    }

    /* Delegates to the head IFormMetadata's property. */ get IsAdminForm(): boolean
    {
        return this.form.IsAdminForm;
    }

    /* Delegates to the head IFormMetadata's property. */ get MenuView(): IViewMetadata
    {
        return this.form.MenuView;
    }

    /* Delegates to the head IFormMetadata's property. */ get ReturnDataOnSave(): boolean
    {
        return this.form.ReturnDataOnSave;
    }

    /* Delegates to the head IFormMetadata's property. */ get BreadCrumbUpdateable(): boolean
    {
        return this.form.BreadCrumbUpdateable;
    }

    /* Delegates to the head IFormMetadata's property. */ get BreadCrumbUpdateableFields(): string[]
    {
        return this.form.BreadCrumbUpdateableFields;
    }

    /* Delegates to the head IFormMetadata's property. */ get FormId(): string
    {
        return this.form.FormId;
    }

    /* Delegates to the head IFormMetadata's property. */ get EnableViewShortcuts(): boolean
    {
        return this.form.EnableViewShortcuts;
    }

    /* Delegates to the head IFormMetadata's property. */ get RecalculateParentFormOnSave(): boolean
    {
        return this.form.RecalculateParentFormOnSave;
    }

    /* Delegates to the head IFormMetadata's property. */ get SaveHandling(): SaveHandlingType
    {
        return this.form.SaveHandling;
    }

    /* Delegates to the head IFormMetadata's property. */ get Name(): string
    {
        return this.form.Name;
    }

    /* Delegates to the head IFormMetadata's property. */ get OnShown(): RecalculateProperty
    {
        return this.form.OnShown;
    }

    /* Delegates to the head IFormMetadata's property. */ get OnCancel(): RecalculateProperty
    {
        return this.form.OnCancel;
    }

    /* Delegates to the head IFormMetadata's property. */ get Path(): string
    {
        return this.form.Path;
    }

    /* Delegates to the head IFormMetadata's property. */ get FooterView(): IViewMetadata
    {
        return this.form.FooterView;
    }

    /* Delegates to the head IFormMetadata's property. */ get Views(): IViewMetadata[]
    {
        return this.form.Views;
    }

    /* Delegates to the head IFormMetadata's property. */ get SaveLabelText(): string
    {
        return this.form.SaveLabelText;
    }

    /* Delegates to the head IFormMetadata's property. */ get SaveLabelProperty(): string
    {
        return this.form.SaveLabelProperty;
    }

    /* Delegates to the head IFormMetadata's property. */ get CancelLabelText(): string
    {
        return this.form.CancelLabelText;
    }

    /* Delegates to the head IFormMetadata's property. */ get VerifyWithParent(): boolean
    {
        return this.form.VerifyWithParent;
    }

    /* Delegates to the head IFormMetadata's property. */ get ViewOnly(): boolean
    {
        return this.form.ViewOnly;
    }

    /* Delegates to the head IFormMetadata's property. */ get ViewOnlyProperty(): string
    {
        return this.form.ViewOnlyProperty;
    }

    /* Delegates to the head IFormMetadata's property. */ get TitleProperty(): string
    {
        return this.form.TitleProperty;
    }

    /* Delegates to the head IFormMetadata's property. */ get HeaderBadges(): HeaderBadge[]
    {
        return this.form.HeaderBadges;
    }

    /* Settable client-side form properties that delegate to the current form. */
    get SaveOptions(): IMenuOption[]
    {
        return this.form["$saveOptions"];
    }

    set SaveOptions(value: IMenuOption[])
    {
        this.form["$saveOptions"] = value;
    }

    get AddNew(): boolean
    {
        return this.form["$addNew"];
    }

    set AddNew(value: boolean)
    {
        this.form["$addNew"] = value;
    }

    get AddNewAndCopy(): boolean
    {
        return this.form["$addNewAndCopy"];
    }

    set AddNewAndCopy(value: boolean)
    {
        this.form["$addNewAndCopy"] = value;
    }

    get SaveButtonText(): string
    {
        return this.form["$saveButtonText"];
    }

    set SaveButtonText(value: string)
    {
        this.form["$saveButtonText"] = value;
    }

    get InitialViewSet(): boolean
    {
        return this.form["$initialViewSet"];
    }

    set InitialViewSet(value: boolean)
    {
        this.form["$initialViewSet"] = value;
    }

    /**
     * Pushes a new state onto the FormStack with given values.
     *
     * @param form New IFormMetadata to display
     * @param model model object for the new IFormMetadata
     * @param dataController collection object for the new IFormMetadata
     * @param addMode opens a form in add mode instead of edit mode
     * @param selectedView [Optional] the IViewMetadata of the IFormMetadata to display first.
     */
    goTo(
        form: Readonly<IFormMetadata>,
        model: Readonly<any>,
        currentTitle: string,
        dataController: Readonly<TCollection>,
        addMode: boolean,
        parentIsDirty: boolean,
        selectedView: IViewMetadata = undefined
    ): void
    {
        let currentIdentifier: string;
        if (this.isCheckPointFormState(this.current))
            currentIdentifier = this.current.identifier;
        this.stack.push(new CheckpointFormState<TModel, TCollection>(form, model, addMode, parentIsDirty, dataController, selectedView, currentIdentifier));
    }

    pop(cancelled: boolean): FormState<TModel, TCollection>
    {
        const popped = this.stack.pop();
        if (this.isCheckPointFormState(popped))
        {
            popped.resolveCheckpoint(cancelled);
            if (this.isCheckPointFormState(this.current))
                StackIdentifier.set(this.current.identifier);
        }

        return popped;
    }

    isCheckPointFormState(state: FormState<TModel, TCollection>): state is CheckpointFormState<TModel, TCollection>
    {
        return "identifier" in state;
    }
}
export class FormState<TModel, TCollection extends RepositoryDataCollection>
{
    constructor(
        public readonly formMetadata: Readonly<IFormMetadata>,
        public readonly model: Readonly<TModel>,
        public readonly addMode: Readonly<boolean>,
        public readonly parentIsDirty: Readonly<boolean>,
        protected _datacontroller: Readonly<TCollection>,
        protected _selectedView: IViewMetadata = undefined
    )
    { }

    get datacontroller(): TCollection
    {
        return this._datacontroller;
    }

    replaceDataController(value: Readonly<TCollection>): void
    {
        this._datacontroller = value;
    }

    get selectedView(): IViewMetadata
    {
        return this._selectedView || ArrayHelpers.first(this.formMetadata.Views);
    }

    set selectedView(value: IViewMetadata)
    {
        this._selectedView = value;
    }
}
export class CheckpointFormState<TModel, TCollection extends RepositoryDataCollection> extends FormState<TModel, TCollection>
{
    identifier: string;

    constructor(
        public readonly formMetadata: Readonly<IFormMetadata>,
        public readonly model: Readonly<TModel>,
        public readonly addMode: Readonly<boolean>,
        public readonly parentIsDirty: Readonly<boolean>,
        protected _datacontroller: Readonly<TCollection>,
        protected _selectedView: IViewMetadata,
        private readonly previousIdentifier: string
    )
    {
        super(formMetadata, model, addMode, parentIsDirty, _datacontroller, _selectedView);
        this.identifier = GuidHelper.newGuid();
        StackIdentifier.set(this.identifier);
    }

    resolveCheckpoint(cancelled: boolean): void
    {
        const action = cancelled ? CheckpointAction.Rollback : CheckpointAction.Commit;
        this.processCheckpoint(action);
    }

    private processCheckpoint(action: CheckpointAction): void
    {
        this.processEntityCheckpoint(this.model, action);
        this.processEntityCheckpoint(this._datacontroller.Entity, action);
        for (const typeName in this._datacontroller.OtherEntities)
        {
            const entities = this._datacontroller.OtherEntities[typeName];
            entities.forEach(entity => this.processEntityCheckpoint(entity, action));
        }
    }

    private processEntityCheckpoint(entity: any, action: CheckpointAction, isParent: boolean = false): void
    {
        if (!entity || !entity["$isEntityWrapper"] || !this.identifier)
            return;

        entity.handleCheckPoint(this.identifier, this.previousIdentifier, action);

        if (isParent)
        {
            if (entity["$$Parent"])
                this.processEntityCheckpoint(entity["$$Parent"], action, true);
            return;
        }
        for (const field in entity)
        {
            if (!!entity[field] && (field === "$$Parent" || !field.startsWith("$")))
            {
                const value = entity[field];
                if (Array.isArray(value) && value.length > 0 && value[0] && value[0]["$isEntityWrapper"])
                {
                    for (let i = value.length - 1; i >= 0; i--)
                    {
                        const item = value[i];
                        if (action === CheckpointAction.Rollback && item.SerializedRowState === "Deleted")
                            item.SerializedRowState = "Unchanged";
                        else if (action === CheckpointAction.Rollback && item.$added)
                            value.splice(i, 1);
                        else
                            this.processEntityCheckpoint(item, action, field === "$$Parent");
                    }
                }

                if (value && value["$isEntityWrapper"])
                    this.processEntityCheckpoint(value, action, field === "$$Parent");
            }
        }
    }
}
class Stack<T>
{
    private readonly _stack: T[];

    constructor(stack?: Stack<T>)
    {
        this._stack = stack ? [...stack._stack] : [];
    }

    get length(): number
    {
        return this._stack.length;
    }

    head(): T
    {
        return this._stack.length > 0 ? this._stack[this._stack.length - 1] : null;
    }

    previous(): Readonly<T>
    {
        return this._stack.length > 1 ? this._stack[this._stack.length - 2] : null;
    }

    push(item: T): void
    {
        this._stack.push(item);
    }

    pop(): T
    {
        return this._stack.pop();
    }

    toJSON(): Array<T>
    {
        return this._stack;
    }
}

export class StackIdentifier
{
    private static _stateIdentifier: string;

    static set(v: string): void
    {
        StackIdentifier._stateIdentifier = v;
    }

    static get(): string
    {
        return StackIdentifier._stateIdentifier;
    }
}
